import DOMPurify from "dompurify";
import {
  imagePrefixesFor,
  isValidImageDimension,
  isAllowedEmailImageSrc,
  normalizeImageStyle,
  styleWidthPx,
} from "../../../../../supabase/functions/_shared/email/emailImages";

const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "a",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "blockquote",
  "span",
  "hr",
  "img",
];
const IMG_ATTR = ["src", "alt", "width", "height", "style"];
const ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "data-variable",
  "class",
  ...IMG_ATTR,
];

/**
 * Image hosts accepted in the editor: crm.gavion.ai and the public email-assets bucket of this
 * build's Supabase project (VITE_SUPABASE_URL) plus production's (the server's allowlist).
 */
export const EMAIL_IMAGE_PREFIXES = imagePrefixesFor(
  import.meta.env.VITE_SUPABASE_URL as string | undefined,
);
// DOMPurify's default pattern minus every scheme except https: and mailto:.
// Scheme-less values ({{var}}, "_blank", "company.name") pass; javascript: never does.
const ALLOWED_URI_REGEXP =
  /^(?:(?:https|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

const PLAIN_VAR =
  /^\{\{\s*[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*\s*\}\}$/;
const BLOCK_HELPER = /\{\{\s*(?:#|\/|else|\^)/i;

/** True when a URL is https:, mailto: or the whole value is a plain {{variable}}. */
export const isSafeLinkUrl = (url: string): boolean => {
  const v = url.trim();
  if (BLOCK_HELPER.test(v)) return false;
  return /^(?:https:\/\/|mailto:)/i.test(v) || PLAIN_VAR.test(v);
};

/** src is stricter: https:// or a plain {{variable}} only (no mailto:). */
export const isSafeSrc = (url: string): boolean => {
  const v = url.trim();
  if (BLOCK_HELPER.test(v)) return false;
  return /^https:\/\//i.test(v) || PLAIN_VAR.test(v);
};

const urlHook = (
  node: Element,
  data: { attrName: string; attrValue: string; keepAttr: boolean },
) => {
  if (data.attrName === "href" && !isSafeLinkUrl(data.attrValue))
    data.keepAttr = false;
  const isImg = node.nodeName === "IMG";
  // img carries only src/alt/width/height/style; those never appear on other elements.
  if (isImg !== IMG_ATTR.includes(data.attrName)) {
    data.keepAttr = false;
    return;
  }
  if (!isImg) return;
  if (data.attrName === "src") {
    if (!isAllowedEmailImageSrc(data.attrValue, EMAIL_IMAGE_PREFIXES))
      data.keepAttr = false;
  } else if (data.attrName === "width" || data.attrName === "height") {
    if (!isValidImageDimension(data.attrValue)) data.keepAttr = false;
  } else if (data.attrName === "style") {
    const style = normalizeImageStyle(data.attrValue);
    if (style === null) data.keepAttr = false;
    else data.attrValue = style;
  }
};

/** An image without an allowed src is removed; a px style width is copied to width (Outlook). */
const imgHook = (node: Element) => {
  if (node.nodeName !== "IMG") return;
  if (!node.hasAttribute("src")) {
    node.remove();
    return;
  }
  const px = styleWidthPx(node.getAttribute("style"));
  if (
    !node.hasAttribute("width") &&
    px !== null &&
    isValidImageDimension(String(px))
  )
    node.setAttribute("width", String(px));
};

/** Sanitises editor output before it is stored as email_templates.body_html. */
export function sanitizeEmailHtml(html: string): string {
  DOMPurify.addHook("uponSanitizeAttribute", urlHook);
  DOMPurify.addHook("afterSanitizeAttributes", imgHook);
  try {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      ALLOWED_URI_REGEXP,
    }) as string;
  } finally {
    DOMPurify.removeHook("uponSanitizeAttribute", urlHook);
    DOMPurify.removeHook("afterSanitizeAttributes", imgHook);
  }
}
